/**
 * Какие данные нужны разделу карты — ВСЕ, что он умеет нарисовать.
 *
 * Изотермы и фронты считаются в браузере по сырой сетке SST, волнение — по полю
 * волны с откатом на ветер. Если этих данных за выбранную дату в районе нет,
 * инструмент рисует пустоту — и объяснить её нечем.
 *
 * Ровно так и вышло 2026-08-08 (Берингово, дата 09.08): SST за эту дату лежала
 * только по полосе 44…60° с.ш., 138…163° в.д. — кадр владельца в неё не попадал,
 * изотермы/фронты/волны молчали, а изобаты (они от даты не зависят) рисовались.
 * Кнопка «Загрузить данные» их не чинила: она качала слои РАЗДЕЛА, про
 * включённые инструменты не зная.
 *
 * ПОЧЕМУ НАБОР НЕ ЗАВИСИТ ОТ ТУМБЛЕРОВ. Первая правка (08.08) добавляла к слою
 * раздела данные ВКЛЮЧЁННЫХ инструментов — и оставляла кнопку заложницей порядка
 * действий: включил изотерму после клика — данных под неё никто не качал, а
 * второй раз кнопку нажимать незачем, она же отчиталась «Готово». Так и вышло
 * 12.08.2026: задача ушла со списком ["currents"], сервер в ту же секунду
 * записал в лог «sst внутренняя дыра поставки → докачка» — и выбросил этот
 * диагноз, потому что клиент про sst не спросил. Правило владельца: нажал
 * «Загрузить» — данные загружены по всему вьюпорту. Поэтому качаем всё, что
 * раздел МОЖЕТ показать. Там, где данные уже есть, это бесплатно: охват
 * считается всё равно (`compute_delta`), и полный параметр в докачку не идёт.
 *
 * ВАЖНО про волны. Сетка волнения (`/cabinet/waves/grid`) читает `wave_field`
 * (реальный VHM0) и, где его нет, `air_surface` (оценка по ветру). Слой
 * `wind_speed` тут бесполезен: он наполняет ДРУГУЮ таблицу — `winds`. На этом и
 * обожглись в первой правке: докачка отчитывалась «OK», а волнение оставалось
 * пустым. Просим ровно то, что читаем.
 *
 * Имена ниже — download-ПАРАМЕТРЫ, а не слои каталога: неизвестный каталогу id
 * бэкенд пропускает в параметры как есть (`layer_to_params`), а генератор тайлов
 * такие ключи отбрасывает — что и нужно, инструменты рисуются браузером.
 */
export const ISOTHERM_LAYER = 'sst'

/** Оба источника сетки волнения: предпочтительный VHM0 и запасной ветер. */
export const WAVE_LAYERS = ['wave_hs', 'air_surface'] as const

/** Данные всех инструментов, которые кабинет рисует поверх ЛЮБОГО базового слоя. */
export const TOOL_LAYERS = [ISOTHERM_LAYER, ...WAVE_LAYERS] as const

/** Слой раздела + всё, что раздел умеет нарисовать поверх; без повторов. */
export function withSectionLayers(base: string[]): string[] {
  return [...new Set([...base, ...TOOL_LAYERS])]
}

/**
 * Ключ запроса сетки инструмента наложения: пока он прежний — в сеть не ходим.
 *
 * Докачать данные мало — сетку надо ПЕРЕЧИТАТЬ. Изотермы, фронты и волнение
 * рисует браузер по полю, взятому один раз на вьюпорт, и до 2026-08-10 это поле
 * запрашивалось только на смену даты, глубины и вьюпорта. Кнопка «Обновить» не
 * меняет ни одного из них, поэтому докачка ложилась в базу, а на экране
 * оставалось до-докачечное поле: замер того дня — кадр 158…169° в.д. на
 * 11 августа, восточная половина `sst`/`wave_field`/`air_surface` приехала в
 * 13:36, а линии так и обрывались по 163° (кромке регулярной предзагрузки).
 * `loadNonce` — счётчик завершившихся докачек: страница бьёт его в `onReady`.
 *
 * У стрелок течений тот же будильник заведён давно (`tilesVersion` в зависимостях
 * запроса) — здесь он просто дотянут до остальных инструментов.
 */
export function overlayGridKey(p: {
  date: string
  /** Глубина слоя; у волнения её нет — Hs поверхностная. */
  depth?: number
  /** Центр+зум карты. */
  viewKey: string
  /** Сколько докачек завершилось с начала жизни страницы. */
  loadNonce: number
}): string {
  return [p.date, p.depth ?? '-', p.viewKey, p.loadNonce].join('|')
}
